/**
 * Raster morphology helpers shared by the SVG editor and Stitch editor
 * "Expand / Shrink" (dilate / erode) tools.
 */

/**
 * Compute a chamfer 3-4 signed distance field from an ImageData alpha mask.
 * Returned distances are ×3 (chamfer scale): negative inside the shape,
 * positive outside. Computing the field once makes every subsequent
 * dilate/erode amount a cheap threshold pass (mask = dist <= radius*3).
 *
 * @param {ImageData} imageData  Source pixels; alpha > 127 counts as "inside".
 * @returns {Int16Array} signed distance ×3, length = width * height
 */
export function computeSignedDistanceField(imageData) {
  const { width: w, height: h, data } = imageData;
  const n = w * h;
  const INF = 30000;
  const out = new Int16Array(n); // distance to nearest foreground (0 inside)
  const inn = new Int16Array(n); // distance to nearest background (0 outside)
  for (let i = 0; i < n; i++) {
    const fg = data[i * 4 + 3] > 127;
    out[i] = fg ? 0 : INF;
    inn[i] = fg ? INF : 0;
  }
  const chamfer = (arr) => {
    // Forward pass
    for (let y = 0; y < h; y++) {
      const row = y * w;
      for (let x = 0; x < w; x++) {
        const i = row + x;
        let v = arr[i];
        if (v === 0) continue;
        if (x > 0 && arr[i - 1] + 3 < v) v = arr[i - 1] + 3;
        if (y > 0) {
          const up = i - w;
          if (arr[up] + 3 < v) v = arr[up] + 3;
          if (x > 0 && arr[up - 1] + 4 < v) v = arr[up - 1] + 4;
          if (x < w - 1 && arr[up + 1] + 4 < v) v = arr[up + 1] + 4;
        }
        arr[i] = v;
      }
    }
    // Backward pass
    for (let y = h - 1; y >= 0; y--) {
      const row = y * w;
      for (let x = w - 1; x >= 0; x--) {
        const i = row + x;
        let v = arr[i];
        if (v === 0) continue;
        if (x < w - 1 && arr[i + 1] + 3 < v) v = arr[i + 1] + 3;
        if (y < h - 1) {
          const dn = i + w;
          if (arr[dn] + 3 < v) v = arr[dn] + 3;
          if (x < w - 1 && arr[dn + 1] + 4 < v) v = arr[dn + 1] + 4;
          if (x > 0 && arr[dn - 1] + 4 < v) v = arr[dn - 1] + 4;
        }
        arr[i] = v;
      }
    }
  };
  chamfer(out);
  chamfer(inn);
  // Merge into one signed field (reuse `out`): >0 outside, <0 inside
  for (let i = 0; i < n; i++) {
    if (inn[i] > 0) out[i] = -inn[i];
  }
  return out;
}

/** Parse a #rgb / #rrggbb color into [r, g, b]. Falls back to black. */
export function parseHexColor(str) {
  if (typeof str === "string") {
    const hex = str.trim().replace(/^#/, "");
    if (/^[0-9a-fA-F]{3}$/.test(hex)) {
      return [
        parseInt(hex[0] + hex[0], 16),
        parseInt(hex[1] + hex[1], 16),
        parseInt(hex[2] + hex[2], 16),
      ];
    }
    if (/^[0-9a-fA-F]{6}$/.test(hex)) {
      return [
        parseInt(hex.slice(0, 2), 16),
        parseInt(hex.slice(2, 4), 16),
        parseInt(hex.slice(4, 6), 16),
      ];
    }
  }
  return [0, 0, 0];
}

/**
 * Apply the SDF threshold for a given morph radius and return a fresh
 * ImageData: pixels inside the new (dilated/eroded) shape keep their original
 * color when they existed before, new pixels take `fillColor`.
 *
 * @param {ImageData} base       Pristine rasterized shape
 * @param {Int16Array} dist      Output of computeSignedDistanceField(base)
 * @param {number} radiusPx      Offset in pixels: >0 dilate, <0 erode
 * @param {string} fillColor     Hex color for newly grown pixels
 * @param {CanvasRenderingContext2D} ctx  Context used to allocate ImageData
 * @returns {ImageData}
 */
export function morphImageData(base, dist, radiusPx, fillColor, ctx) {
  const w = base.width;
  const h = base.height;
  const r3 = Math.round(radiusPx * 3);
  const src = base.data;
  const outImg = ctx.createImageData(w, h);
  const dst = outImg.data;
  const [cr, cg, cb] = parseHexColor(fillColor);
  for (let i = 0, n = w * h; i < n; i++) {
    if (dist[i] <= r3) {
      const j = i * 4;
      if (src[j + 3] > 127) {
        dst[j] = src[j];
        dst[j + 1] = src[j + 1];
        dst[j + 2] = src[j + 2];
      } else {
        dst[j] = cr;
        dst[j + 1] = cg;
        dst[j + 2] = cb;
      }
      dst[j + 3] = 255;
    }
  }
  return outImg;
}
